import { useState } from 'react';
import { UserCircle, Activity, Plug, Users, Folder, MessageSquare, Network, GraduationCap, Zap, FolderOpen, Database, Globe, Monitor, Search, Plus, Play, Pause, MoreHorizontal, Check, Clock, ArrowUpRight, ChevronDown, X, Lock, Layers, KeyRound, Copy, Pencil, Grid3x3 } from 'lucide-react';
import { TRACKS, GROUP_NAMES } from '../../data/workspaceTracks';
import { PulsesView } from './PulsesView';

// ── In-shell workspace views ─────────────────────────────────────────────────
// Every nav destination in the Giga chat shell renders here rather than routing
// out to the old dashboard. One shell, many views: the sidebar swaps the pane,
// nothing navigates.
// ─────────────────────────────────────────────────────────────────────────────

export type WorkspaceViewId = 'assistants' | 'pulses' | 'connections' | 'groups' | 'tracks' | 'chats' | 'workflows' | 'skills' | 'power-ups' | 'files' | 'tables' | 'browser' | 'viewer';
export const VIEW_META: Record<WorkspaceViewId, {
  title: string;
  blurb: string;
  icon: typeof UserCircle;
  action?: string;
}> = {
  assistants: {
    title: 'Assistants',
    blurb: 'Coworkers you brief once, then delegate to.',
    icon: UserCircle,
    action: 'New assistant'
  },
  pulses: {
    title: 'Pulses',
    blurb: 'Work that runs on a schedule without being asked.',
    icon: Activity,
    action: 'New pulse'
  },
  connections: {
    title: 'Connections',
    blurb: 'Accounts Giga signs into on your behalf.',
    icon: Plug,
    action: 'Connect a tool'
  },
  groups: {
    title: 'Groups',
    blurb: 'Who can see which knowledge. Access follows the group.',
    icon: Users,
    action: 'New group'
  },
  tracks: {
    title: 'Tracks',
    blurb: 'Projects and areas that keep their work together.',
    icon: Folder,
    action: 'New track'
  },
  chats: {
    title: 'Chats',
    blurb: 'Every conversation, searchable.',
    icon: MessageSquare
  },
  workflows: {
    title: 'Workflows',
    blurb: 'Multi-step jobs you run on demand.',
    icon: Network,
    action: 'New workflow'
  },
  skills: {
    title: 'Skills',
    blurb: 'Recorded how-tos Giga reuses across jobs.',
    icon: GraduationCap,
    action: 'Record a skill'
  },
  'power-ups': {
    title: 'Power Ups',
    blurb: 'Extra abilities you can switch on per workspace.',
    icon: Zap
  },
  files: {
    title: 'Files',
    blurb: 'Documents Giga can read and cite.',
    icon: FolderOpen,
    action: 'Upload'
  },
  tables: {
    title: 'Tables',
    blurb: 'Structured data it can query and write rows to.',
    icon: Database,
    action: 'New table'
  },
  browser: {
    title: 'Browser',
    blurb: 'Sites Giga operates on your behalf.',
    icon: Globe
  },
  viewer: {
    title: 'Viewer',
    blurb: 'Watch a run happen live, step by step.',
    icon: Monitor
  }
};
const logo = (slug: string) => `https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${slug}.svg`;
const ASSISTANTS = [{
  name: 'Inbox triage',
  desc: 'Sorts overnight mail and surfaces only what needs a reply.',
  tools: ['gmail', 'googlecalendar'],
  runs: '412 runs',
  group: 'Faith’s Private Space',
  private: true,
  active: true
}, {
  name: 'Pipeline analyst',
  desc: 'Watches deal movement and flags renewals going quiet.',
  tools: ['salesforce', 'slack'],
  runs: '96 runs',
  group: 'Giga Master',
  private: false,
  active: true
}, {
  name: 'Meeting prep',
  desc: 'Builds a one-pager for every external meeting the night before.',
  tools: ['googlecalendar', 'notion'],
  runs: '188 runs',
  group: 'Giga Marketing',
  private: false,
  active: true
}, {
  name: 'Support summariser',
  desc: 'Turns Intercom threads into weekly themes for product.',
  tools: ['intercom', 'linear'],
  runs: '54 runs',
  group: 'NoContact Ops',
  private: false,
  active: false
}];

// The catalogue. `accounts` matters more than a boolean: several tools support
// more than one signed-in account, and knowing there are two is the difference
// between "connected" and "connected as the wrong person".
type ConnCategory = 'Communication' | 'Productivity' | 'CRM & Sales' | 'Engineering' | 'Marketing' | 'Finance' | 'Data' | 'Design' | 'Support';
const CONNECTIONS: {
  name: string;
  slug: string;
  desc: string;
  category: ConnCategory;
  accounts: number;
  account?: string;
  popular?: boolean;
}[] = [{
  name: 'Gmail',
  slug: 'gmail',
  desc: 'Read, draft and send mail from your own address.',
  category: 'Communication',
  accounts: 2,
  account: 'sarah.chen@brightwave.io',
  popular: true
}, {
  name: 'Google Drive',
  slug: 'google-drive',
  desc: 'Search and read documents across shared drives.',
  category: 'Productivity',
  accounts: 1,
  account: 'sarah.chen@brightwave.io',
  popular: true
}, {
  name: 'Google Calendar',
  slug: 'google-calendar',
  desc: 'See availability and schedule on your behalf.',
  category: 'Productivity',
  accounts: 1,
  account: 'sarah.chen@brightwave.io',
  popular: true
}, {
  name: 'Notion',
  slug: 'notion',
  desc: 'A collaborative workspace for docs and wikis.',
  category: 'Productivity',
  accounts: 1,
  account: 'Brightwave workspace',
  popular: true
}, {
  name: 'Slack',
  slug: 'slack',
  desc: 'Workplace messaging — post, read and reply in threads.',
  category: 'Communication',
  accounts: 1,
  account: 'brightwave.slack.com',
  popular: true
}, {
  name: 'Linear',
  slug: 'linear',
  desc: 'Product development and issue tracking.',
  category: 'Engineering',
  accounts: 1,
  account: 'brightwave',
  popular: true
}, {
  name: 'Google Sheets',
  slug: 'google-sheets',
  desc: 'Read and write rows in spreadsheets.',
  category: 'Data',
  accounts: 1,
  account: 'sarah.chen@brightwave.io'
}, {
  name: 'Intercom',
  slug: 'intercom',
  desc: 'Customer service platform for support conversations.',
  category: 'Support',
  accounts: 1,
  account: 'Brightwave'
}, {
  name: 'Salesforce',
  slug: 'salesforce',
  desc: 'The CRM of record for accounts and opportunities.',
  category: 'CRM & Sales',
  accounts: 0,
  popular: true
}, {
  name: 'HubSpot',
  slug: 'hubspot',
  desc: 'Customer platform and CRM for marketing and sales.',
  category: 'CRM & Sales',
  accounts: 0,
  popular: true
}, {
  name: 'Jira',
  slug: 'jira',
  desc: 'Issue and project tracking for software teams.',
  category: 'Engineering',
  accounts: 0,
  popular: true
}, {
  name: 'GitHub',
  slug: 'github',
  desc: 'Code hosting, pull requests and issues.',
  category: 'Engineering',
  accounts: 0
}, {
  name: 'Figma',
  slug: 'figma',
  desc: 'Collaborative interface design and prototyping.',
  category: 'Design',
  accounts: 0
}, {
  name: 'Airtable',
  slug: 'airtable',
  desc: 'Structured bases Giga can query and update.',
  category: 'Data',
  accounts: 0
}, {
  name: 'Stripe',
  slug: 'stripe',
  desc: 'Financial infrastructure — payments and subscriptions.',
  category: 'Finance',
  accounts: 0
}, {
  name: 'Zoom',
  slug: 'zoom',
  desc: 'Meetings, recordings and transcripts.',
  category: 'Communication',
  accounts: 0
}, {
  name: 'Asana',
  slug: 'asana',
  desc: 'Work management for tasks and projects.',
  category: 'Productivity',
  accounts: 0
}, {
  name: 'Mailchimp',
  slug: 'mailchimp',
  desc: 'Email campaigns, audiences and reporting.',
  category: 'Marketing',
  accounts: 0
}, {
  name: 'Dropbox',
  slug: 'dropbox',
  desc: 'File storage Giga can read from and write to.',
  category: 'Productivity',
  accounts: 0
}, {
  name: 'QuickBooks',
  slug: 'quickbooks',
  desc: 'Accounting, invoices and expense records.',
  category: 'Finance',
  accounts: 0
}, {
  name: 'Canva',
  slug: 'canva',
  desc: 'Visual design and collaboration platform.',
  category: 'Design',
  accounts: 0
}, {
  name: 'Monday.com',
  slug: 'monday',
  desc: 'Work management platform for teams.',
  category: 'Productivity',
  accounts: 0
}, {
  name: 'ClickUp',
  slug: 'clickup',
  desc: 'Tasks, docs and goals in one workspace.',
  category: 'Productivity',
  accounts: 0
}, {
  name: 'Zapier',
  slug: 'zapier',
  desc: 'Trigger automations across thousands of apps.',
  category: 'Productivity',
  accounts: 1,
  account: 'brightwave-ops'
}, {
  name: 'Vercel',
  slug: 'vercel',
  desc: 'Deployments, previews and build logs.',
  category: 'Engineering',
  accounts: 0
}, {
  name: 'Sentry',
  slug: 'sentry',
  desc: 'Error monitoring and release health.',
  category: 'Engineering',
  accounts: 1,
  account: 'brightwave'
}, {
  name: 'Supabase',
  slug: 'supabase',
  desc: 'Backend platform with a Postgres database.',
  category: 'Data',
  accounts: 0
}, {
  name: 'Cloudflare',
  slug: 'cloudflare',
  desc: 'DNS, caching and edge configuration.',
  category: 'Engineering',
  accounts: 0
}, {
  name: 'Ahrefs',
  slug: 'ahrefs',
  desc: 'Search rankings, backlinks and keyword data.',
  category: 'Marketing',
  accounts: 1,
  account: 'brightwave.io'
}, {
  name: 'Apollo',
  slug: 'apollo',
  desc: 'Sales intelligence and prospect enrichment.',
  category: 'CRM & Sales',
  accounts: 0
}, {
  name: 'Clay',
  slug: 'clay',
  desc: 'Enrich and research accounts at scale.',
  category: 'CRM & Sales',
  accounts: 0
}, {
  name: 'Webflow',
  slug: 'webflow',
  desc: 'Website building, CMS and publishing.',
  category: 'Marketing',
  accounts: 0
}, {
  name: 'Ramp',
  slug: 'ramp',
  desc: 'Corporate cards, spend and receipts.',
  category: 'Finance',
  accounts: 0
}, {
  name: 'Zendesk',
  slug: 'zendesk',
  desc: 'Ticketing and helpdesk for customer support.',
  category: 'Support',
  accounts: 0
}, {
  name: 'Granola',
  slug: 'granola',
  desc: 'AI notepad that captures meeting notes.',
  category: 'Productivity',
  accounts: 1,
  account: 'sarah.chen@brightwave.io'
}, {
  name: 'Confluence',
  slug: 'confluence',
  desc: 'Team wiki and internal documentation.',
  category: 'Productivity',
  accounts: 0
}];
const CONN_CATEGORIES: ConnCategory[] = ['Communication', 'Productivity', 'CRM & Sales', 'Engineering', 'Marketing', 'Finance', 'Data', 'Design', 'Support'];
const CONN_PAGE_SIZE = 12;

// Which group owns each live credential. Only connected tools appear here —
// an integration nobody has signed into doesn't belong to anyone yet.
const CONN_GROUP: Record<string, string> = {
  Gmail: 'Everyone',
  'Google Drive': 'Everyone',
  'Google Calendar': 'Everyone',
  Notion: 'Giga Master',
  Slack: 'Everyone',
  Linear: 'Giga Master',
  'Google Sheets': 'Giga Marketing',
  Intercom: 'NoContact Ops',
  Zapier: 'NoContact Ops',
  Sentry: 'Giga Master',
  Ahrefs: 'Giga Marketing',
  Granola: "Faith's Private Space"
};

// Custom credentials: the escape hatch for anything without a first-party
// integration. Each type asks for a different shape of secret.
const CREDENTIAL_TYPES = [{
  id: 'email',
  label: 'Email & Password',
  desc: 'For tools Giga signs into through the browser.',
  fields: [{
    label: 'Login URL',
    placeholder: 'https://app.example.com/login',
    type: 'url'
  }, {
    label: 'Email',
    placeholder: 'you@company.com',
    type: 'email'
  }, {
    label: 'Password',
    placeholder: '••••••••••••',
    type: 'password'
  }]
}, {
  id: 'api',
  label: 'API Key',
  desc: 'For tools with a token-based API.',
  fields: [{
    label: 'Base URL',
    placeholder: 'https://api.example.com',
    type: 'url'
  }, {
    label: 'API key',
    placeholder: 'sk_live_…',
    type: 'password'
  }, {
    label: 'Header name',
    placeholder: 'Authorization',
    type: 'text'
  }]
}, {
  id: 'sql-string',
  label: 'SQL (connection string)',
  desc: 'One URI for the whole database.',
  fields: [{
    label: 'Connection string',
    placeholder: 'postgres://user:pass@host:5432/db',
    type: 'password'
  }]
}, {
  id: 'sql-fields',
  label: 'SQL (fields)',
  desc: 'Host, port and credentials entered separately.',
  fields: [{
    label: 'Host',
    placeholder: 'db.example.com',
    type: 'text'
  }, {
    label: 'Port',
    placeholder: '5432',
    type: 'text'
  }, {
    label: 'Database',
    placeholder: 'production',
    type: 'text'
  }, {
    label: 'User',
    placeholder: 'readonly',
    type: 'text'
  }, {
    label: 'Password',
    placeholder: '••••••••••••',
    type: 'password'
  }]
}] as const;
const CHATS = [{
  name: 'Inbox catch-up',
  when: 'Today, 09:12',
  preview: 'Three things need you today — Acme redlines, Northwind seats, invoice 2214.'
}, {
  name: 'Tomorrow’s meetings',
  when: 'Today, 08:40',
  preview: 'Four meetings. Two need prep — Acme legal and Northwind kickoff.'
}, {
  name: 'Q3 pipeline review',
  when: 'Yesterday',
  preview: 'Revenue up 34% QoQ, CAC down 12%, one renewal unsigned.'
}, {
  name: 'Renewal risk in Salesforce',
  when: 'Yesterday',
  preview: 'Two accounts have gone quiet for 21+ days.'
}, {
  name: 'Draft reply to Acme legal',
  when: '2 days ago',
  preview: 'Position on the liability cap, plus data residency language.'
}, {
  name: 'Summarise the board deck',
  when: '3 days ago',
  preview: 'Slide 19 has an ask with no numbers behind it.'
}, {
  name: 'Who owns the migration?',
  when: 'Last week',
  preview: 'Priya, per the Linear project and the #eng thread.'
}, {
  name: 'Weekly marketing recap',
  when: 'Last week',
  preview: 'Paid down 8%, organic up 15%, two campaigns ended.'
}];
const WORKFLOWS = [{
  name: 'Tweet → Notion research table',
  steps: 4,
  runs: 128,
  success: '96%',
  last: '2 hours ago'
}, {
  name: 'New lead → enriched CRM record',
  steps: 6,
  runs: 341,
  success: '99%',
  last: '18 minutes ago'
}, {
  name: 'Support thread → Linear ticket',
  steps: 3,
  runs: 87,
  success: '92%',
  last: 'Yesterday'
}, {
  name: 'Invoice → finance tracker row',
  steps: 5,
  runs: 64,
  success: '100%',
  last: '4 days ago'
}];
const SKILLS = [{
  name: 'File a Linear bug from a support thread',
  used: 'Used 44 times',
  learned: 'Recorded by Priya'
}, {
  name: 'Build a weekly pipeline recap',
  used: 'Used 31 times',
  learned: 'Recorded by Sarah'
}, {
  name: 'Enrich a lead from a company domain',
  used: 'Used 128 times',
  learned: 'Recorded by Marcus'
}, {
  name: 'Draft a renewal follow-up',
  used: 'Used 19 times',
  learned: 'Recorded by Sarah'
}];
const POWER_UPS = [{
  name: 'Computer use',
  desc: 'Operates sites that have no API, in a sandboxed browser.',
  on: true
}, {
  name: 'Deep research',
  desc: 'Multi-source research runs that cite every claim.',
  on: true
}, {
  name: 'Voice notes',
  desc: 'Transcribes and acts on voice messages.',
  on: false
}, {
  name: 'Code interpreter',
  desc: 'Runs analysis on spreadsheets and CSV exports.',
  on: false
}];
const FILES = [{
  name: 'Q3 Board Review.pdf',
  meta: '24 slides',
  size: '4.2 MB',
  group: 'Giga Master',
  visibility: 'Shared',
  when: 'Yesterday'
}, {
  name: 'Acme MSA redlines.docx',
  meta: '18 pages',
  size: '0.24 MB',
  group: 'No Contact Private',
  visibility: 'Private',
  when: '2 days ago'
}, {
  name: 'Pricing model v4.xlsx',
  meta: '9 sheets',
  size: '1.1 MB',
  group: 'Giga Master',
  visibility: 'Shared',
  when: 'Last week'
}, {
  name: 'Meeting notes — Northwind.md',
  meta: '2 pages',
  size: '0.03 MB',
  group: "Faith's Private Space",
  visibility: 'Private',
  when: '3 days ago'
}, {
  name: 'Brand guidelines.pdf',
  meta: '32 pages',
  size: '8.4 MB',
  group: 'Giga Marketing',
  visibility: 'Shared',
  when: 'Last month'
}];
const TABLES = [{
  name: 'Pipeline',
  rows: '1,284 rows',
  cols: '18 columns',
  source: 'Salesforce',
  group: 'Giga Master'
}, {
  name: 'Content calendar',
  rows: '96 rows',
  cols: '11 columns',
  source: 'Notion',
  group: 'Giga Marketing'
}, {
  name: 'Competitor research',
  rows: '312 rows',
  cols: '14 columns',
  source: 'Giga native',
  group: 'Giga Marketing'
}, {
  name: 'Invoice tracker',
  rows: '208 rows',
  cols: '9 columns',
  source: 'Airtable',
  group: 'NoContact Ops'
}];

// Per-group counts. Deep, muted tile colours so seven monograms stay
// distinguishable without turning the list into a paint chart.
const GROUP_DETAIL: Record<string, {
  path: string;
  color: string;
  members: number;
  chats: number;
  tracks: number;
  credentials: number;
  files: number;
}> = {
  'Giga Master': {
    path: 'spaces/operator',
    color: '#1F3A5F',
    members: 3,
    chats: 214,
    tracks: 33,
    credentials: 0,
    files: 0
  },
  'Giga Marketing': {
    path: 'spaces/operator-gtm',
    color: '#22303C',
    members: 4,
    chats: 89,
    tracks: 20,
    credentials: 5,
    files: 13
  },
  Everyone: {
    path: 'spaces/everyone',
    color: '#8E1F45',
    members: 21,
    chats: 79,
    tracks: 2,
    credentials: 8,
    files: 5
  },
  'No Contact Private': {
    path: 'spaces/no-contact-private',
    color: '#332C7A',
    members: 2,
    chats: 20,
    tracks: 3,
    credentials: 4,
    files: 28
  },
  'NoContact Ops': {
    path: 'spaces/no-contact-ops',
    color: '#8C2340',
    members: 3,
    chats: 17,
    tracks: 4,
    credentials: 6,
    files: 4
  },
  "Faith's Private Space": {
    path: 'spaces/faith-private',
    color: '#4A3A24',
    members: 1,
    chats: 35,
    tracks: 1,
    credentials: 5,
    files: 7
  },
  Demo: {
    path: 'spaces/entropy',
    color: '#7A1F32',
    members: 2,
    chats: 4,
    tracks: 0,
    credentials: 0,
    files: 0
  }
};
const BROWSER_SITES = [{
  name: 'portal.acme-legal.com',
  desc: 'Downloads countersigned contracts',
  runs: '12 runs'
}, {
  name: 'app.quickbooks.com',
  desc: 'Pulls invoice status for the finance tracker',
  runs: '64 runs'
}, {
  name: 'suppliers.northwind.io',
  desc: 'Checks stock levels weekly',
  runs: '8 runs'
}];

// ── Small shared pieces ──────────────────────────────────────────────────────

function Card({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className="rounded-2xl border border-[#EFEAE1] bg-white p-5" data-id="element-3477" data-mp="3u8ere72pgl4q">{children}</div>;
}
function Row({
  children
}: {
  children: React.ReactNode;
}) {
  return <li className="flex items-center gap-4 border-b border-[#F4F0E9] px-5 py-3.5 last:border-b-0" data-id="element-3478" data-mp="19f1jsx4dsgyd">
      {children}
    </li>;
}
function List({
  children
}: {
  children: React.ReactNode;
}) {
  return <ul className="overflow-hidden rounded-2xl border border-[#EFEAE1] bg-white" data-id="element-3479" data-mp="37yg59713az9p">
      {children}
    </ul>;
}
function StatusDot({
  on
}: {
  on: boolean;
}) {
  return <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${on ? 'bg-emerald-500' : 'bg-neutral-300'}`} data-id="element-3480" data-mp="3hnzoidspsv53" />;
}
function ToolLogo({
  slug,
  size = 15
}: {
  slug: string;
  size?: number;
}) {
  return <img src={logo(slug)} alt="" aria-hidden="true" style={{
    width: size,
    height: size
  }} className="object-contain" data-id="element-3481" data-mp="3rl4bgfudwumy" />;
}

// ── The view itself ──────────────────────────────────────────────────────────

export function WorkspaceViews({
  view
}: {
  view: WorkspaceViewId;
}) {
  const [query, setQuery] = useState('');
  const [connTab, setConnTab] = useState<'all' | 'popular' | 'connected'>('all');
  const [connCategory, setConnCategory] = useState<ConnCategory | 'all'>('all');
  const [connCatOpen, setConnCatOpen] = useState(false);
  const [connGroup, setConnGroup] = useState<string>('all');
  const [connPage, setConnPage] = useState(1);
  const [customOpen, setCustomOpen] = useState(false);
  const [customType, setCustomType] = useState<(typeof CREDENTIAL_TYPES)[number]['id'] | null>(null);
  const meta = VIEW_META[view];
  // Pulses carries its own list + detail surface, so it owns the whole pane.
  if (view === 'pulses') return <PulsesView data-id="element-3482" data-mp="2hmurkliddp7v" />;
  const searchable = view === 'tracks' || view === 'chats' || view === 'files' || view === 'connections' || view === 'groups';
  const q = query.trim().toLowerCase();
  return <div className="mx-auto max-w-4xl px-6 pb-16 pt-20 md:pt-16" data-id="element-3483" data-mp="27nvkv8h603ni">
      <header className="flex flex-wrap items-end justify-between gap-4 pb-6" data-id="element-3484" data-mp="23omnzuqtvpnq">
        <div data-id="element-3485" data-mp="wa1e5i48m8kg">
          <h1 className="font-title text-[26px] font-medium tracking-tight text-[#2B211A] md:text-[30px]" data-id="element-3486" data-mp="wajkx8oj056m">
            {meta.title}
          </h1>
          <p className="mt-1.5 text-[14.5px] text-neutral-500" data-id="element-3487" data-mp="sasnim4huqt4">{meta.blurb}</p>
        </div>
        {view === 'connections' ?
      /* Anything without a first-party integration comes in here, so
      the control names the secret it needs rather than just "Add". */
      <div className="relative" data-id="element-3488" data-mp="3f0u5kue7f56m">
            <button type="button" onClick={() => setCustomOpen(o => !o)} aria-expanded={customOpen} className="inline-flex items-center gap-1.5 rounded-xl border border-[#EFEAE1] bg-white px-3.5 py-2 text-[13.5px] font-bold text-[#2B211A] transition-colors hover:border-[#2B211A]" data-id="element-3489" data-mp="3expx3ktphna5">
              <Plus size={14} strokeWidth={2.5} data-id="element-3490" data-mp="6ksc6nnr2oed" />
              Add custom
              <ChevronDown size={14} className={`text-neutral-400 transition-transform duration-200 ${customOpen ? 'rotate-180' : ''}`} data-id="element-3491" data-mp="2hcju3ipra7h" />
            </button>
            {customOpen && <>
                <div className="fixed inset-0 z-30" onClick={() => setCustomOpen(false)} data-id="element-3492" data-mp="2n8wexhjqtzxx" />
                <div className="absolute right-0 top-[calc(100%+6px)] z-40 w-[280px] overflow-hidden rounded-2xl border border-[#EFEAE1] bg-white shadow-[0_24px_60px_-24px_rgba(43,33,26,0.35)]" data-id="element-3493" data-mp="2i9a41ere0vsa">
                  <p className="border-b border-[#F4F0E9] px-4 py-2.5 text-[10.5px] font-bold uppercase tracking-wider text-neutral-400" data-id="element-3494" data-mp="12q2d260rxhh5">
                    Credential type
                  </p>
                  {CREDENTIAL_TYPES.map(t => <button key={t.id} type="button" onClick={() => {
              setCustomType(t.id);
              setCustomOpen(false);
            }} className="block w-full px-4 py-2.5 text-left transition-colors hover:bg-[#FFF7F0]" data-id="element-3495" data-mp="1bjp9lyct2ore">
                      <span className="block text-[13.5px] font-semibold text-[#2B211A]" data-id="element-3496" data-mp="1liwncmngclf1">
                        {t.label}
                      </span>
                      <span className="block text-[12px] text-neutral-500" data-id="element-3497" data-mp="1ugqc9ciw0cs6">
                        {t.desc}
                      </span>
                    </button>)}
                </div>
              </>}
          </div> : meta.action && <button type="button" className="inline-flex items-center gap-1.5 rounded-xl bg-[#2B211A] px-3.5 py-2 text-[13.5px] font-bold text-white transition-colors hover:bg-black" data-id="element-3498" data-mp="40jwz863bmw">
              <Plus size={14} strokeWidth={2.5} data-id="element-3499" data-mp="yh4gti2ixd7a" />
              {meta.action}
            </button>}
      </header>

      {/* The form for the chosen credential type, inline above the list so the
       catalogue stays visible behind the decision. */}
      {view === 'connections' && customType && (() => {
      const t = CREDENTIAL_TYPES.find(c => c.id === customType)!;
      return <div className="mb-4 rounded-2xl border border-[#EFEAE1] bg-white p-5" data-id="element-3500" data-mp="1hf7bacduc2ch">
              <div className="flex items-start justify-between gap-3" data-id="element-3501" data-mp="1w3tqme3gwu3p">
                <div data-id="element-3502" data-mp="1w54s279fxprn">
                  <h2 className="text-[15px] font-semibold text-[#2B211A]" data-id="element-3503" data-mp="1r4h0nkhav6ww">
                    Add custom tool · {t.label}
                  </h2>
                  <p className="mt-1 text-[13px] text-neutral-500" data-id="element-3504" data-mp="254jfsyy83m8g">{t.desc}</p>
                </div>
                <button type="button" onClick={() => setCustomType(null)} aria-label="Cancel" className="text-neutral-300 transition-colors hover:text-[#2B211A]" data-id="element-3505" data-mp="206hx4f4ploxi">
                  <X size={16} data-id="element-3506" data-mp="2oq1uqyrbo0hn" />
                </button>
              </div>
              <div className="mt-4 grid gap-3 sm:grid-cols-2" data-id="element-3507" data-mp="2aqam7djkrcb1">
                <label className="block sm:col-span-2" data-id="element-3508" data-mp="2apr49wtxcnx0">
                  <span className="mb-1.5 block text-[12.5px] font-semibold text-[#2B211A]" data-id="element-3509" data-mp="2equjqi828hxp">
                    Name
                  </span>
                  <input placeholder="What should Giga call this?" className="w-full rounded-xl border border-[#EFEAE1] px-3 py-2.5 text-[14px] text-[#2B211A] outline-none transition-colors placeholder:text-neutral-400 focus:border-[#2B211A]" data-id="element-3510" data-mp="20ywggo115g9g" />
                </label>
                {t.fields.map(f => <label key={f.label} className={`block ${t.fields.length === 1 ? 'sm:col-span-2' : ''}`} data-id="element-3511" data-mp="1w1nu0b8umq2e">
                    <span className="mb-1.5 block text-[12.5px] font-semibold text-[#2B211A]" data-id="element-3512" data-mp="1i24yqj14z97i">
                      {f.label}
                    </span>
                    <input type={f.type} placeholder={f.placeholder} className="w-full rounded-xl border border-[#EFEAE1] px-3 py-2.5 text-[14px] text-[#2B211A] outline-none transition-colors placeholder:text-neutral-400 focus:border-[#2B211A]" data-id="element-3513" data-mp="1f006zmptvwpi" />
                  </label>)}
              </div>
              <div className="mt-4 flex items-center justify-between gap-4 border-t border-[#F4F0E9] pt-3.5" data-id="element-3514" data-mp="118ved4kvo4oo">
                <p className="flex items-center gap-1.5 text-[12px] text-neutral-500" data-id="element-3515" data-mp="w9irwkxx4zmm">
                  <Lock size={12} className="text-neutral-400" data-id="element-3516" data-mp="r9mpur8s7i9p" />
                  Stored in the vault. Never shown in a chat.
                </p>
                <button type="button" className="rounded-xl bg-[#2B211A] px-4 py-2 text-[13.5px] font-bold text-white transition-colors hover:bg-black" data-id="element-3517" data-mp="mbcsqb3hy7lg">
                  Save credential
                </button>
              </div>
            </div>;
    })()}

      {searchable && <div className="relative mb-4" data-id="element-3518" data-mp="2cok2hz9vb9r2">
          <Search size={15} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400" data-id="element-3519" data-mp="2clebufllbzt8" />
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder={`Search ${meta.title.toLowerCase()}…`} className="w-full rounded-xl border border-[#EFEAE1] bg-white py-2.5 pl-10 pr-4 text-[14px] text-[#2B211A] outline-none transition-colors placeholder:text-neutral-400 focus:border-[#2B211A]" data-id="element-3520" data-mp="3euedozidka0" />
        </div>}

      {/* Assistants — cards, because each one has a brief worth reading. */}
      {view === 'assistants' && <div className="grid gap-3 sm:grid-cols-2" data-id="element-3521" data-mp="3gmdzrua0crb3">
          {ASSISTANTS.map(a => <Card key={a.name} data-id="element-3522" data-mp="3kn7lqi3w3w1c">
              <div className="flex items-start justify-between gap-3" data-id="element-3523" data-mp="3klwnsyx4lii5">
                <span className="flex items-center gap-2" data-id="element-3524" data-mp="3pmka57aetb61">
                  <StatusDot on={a.active} data-id="element-3525" data-mp="3pjy1f4bs8pm8" />
                  <span className="text-[15px] font-semibold text-[#2B211A]" data-id="element-3526" data-mp="zx8lyvx00t9t">
                    {a.name}
                  </span>
                </span>
                <button type="button" aria-label={`More options for ${a.name}`} className="text-neutral-300 transition-colors hover:text-[#2B211A]" data-id="element-3527" data-mp="14xwifx3zy2bf">
                  <MoreHorizontal size={16} data-id="element-3528" data-mp="2wy4k9235fm80" />
                </button>
              </div>
              {/* Who can see it comes before the description: an assistant
               someone else owns is read differently to one of your own. */}
              <p className="mt-1.5 flex items-center gap-1.5 text-[12.5px] text-neutral-500" data-id="element-3529" data-mp="35yui0bbyzmod">
                {a.private ? <Lock size={11} className="shrink-0 text-neutral-400" data-id="element-3530" data-mp="2i6dydwcl3mmy" /> : <Users size={11} className="shrink-0 text-neutral-400" data-id="element-3531" data-mp="2daf11j9a2uae" />}
                <span className="truncate" data-id="element-3532" data-mp="2rys04p3nwt94">{a.group}</span>
                {a.private && <span className="shrink-0 rounded-full bg-[#F4F0E9] px-1.5 py-px text-[10.5px] font-semibold text-neutral-500" data-id="element-3533" data-mp="2s035uugo4b2v">
                    Private
                  </span>}
              </p>
              <p className="mt-2.5 text-[13.5px] leading-relaxed text-neutral-500" data-id="element-3534" data-mp="1zfmgu7ig0pxh">
                {a.desc}
              </p>
              <div className="mt-4 flex items-center justify-between gap-3 border-t border-[#F4F0E9] pt-3" data-id="element-3535" data-mp="a0br82w6rv45">
                <span className="flex items-center gap-1.5" data-id="element-3536" data-mp="ertr0nmh3pnj">
                  {a.tools.map(t => <ToolLogo key={t} slug={t} data-id="element-3537" data-mp="10f9kl87rwil" />)}
                </span>
                <span className="text-[12px] text-neutral-400" data-id="element-3538" data-mp="y2inikhabte">{a.runs}</span>
              </div>
            </Card>)}
        </div>}

      {view === 'connections' && <>
          {/* Three ways in: what most teams start with, what's already live,
           and the full catalogue. Counts sit inline so the tabs double as a
           read on how far along the workspace is. */}
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3 border-b border-[#EFEAE1]" data-id="element-3539" data-mp="jq502w4y1w4p">
            <div className="flex items-center gap-5" data-id="element-3540" data-mp="onwubwt29uc2">
              {([['all', 'All integrations', CONNECTIONS.length], ['popular', 'Popular', CONNECTIONS.filter(c => c.popular).length], ['connected', 'Connected', CONNECTIONS.filter(c => c.accounts > 0).length]] as const).map(([id, label, count]) => <button key={id} type="button" onClick={() => {
            setConnTab(id);
            setConnPage(1);
          }} className={`-mb-px flex items-center gap-1.5 border-b-2 pb-2.5 text-[13.5px] font-semibold transition-colors ${connTab === id ? 'border-[#2B211A] text-[#2B211A]' : 'border-transparent text-neutral-500 hover:text-[#2B211A]'}`} data-id="element-3541" data-mp="427lrjgg69zs">
                  {label}
                  <span className="text-[12.5px] font-medium text-neutral-400" data-id="element-3542" data-mp="nnc68m5bh23v">
                    {count}
                  </span>
                </button>)}
            </div>
            {/* Category narrows a long catalogue by the kind of work a tool does,
             which is how people look for one they haven't connected yet. */}
            <div className="relative -mb-px pb-1.5" data-id="element-3543" data-mp="8xwotwroe90u">
              <button type="button" onClick={() => setConnCatOpen(o => !o)} aria-expanded={connCatOpen} className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-[13px] font-medium text-neutral-500 transition-colors hover:text-[#2B211A]" data-id="element-3544" data-mp="16fwsvlufaaz8">
                {connCategory === 'all' ? 'Category' : connCategory}
                <ChevronDown size={14} className={`text-neutral-400 transition-transform duration-200 ${connCatOpen ? 'rotate-180' : ''}`} data-id="element-3545" data-mp="1dywbvqdz20i2" />
              </button>
              {connCatOpen && <>
                  <div className="fixed inset-0 z-30" onClick={() => setConnCatOpen(false)} data-id="element-3546" data-mp="1ny5165xw6qy3" />
                  <div className="absolute right-0 top-[calc(100%+2px)] z-40 w-[190px] overflow-hidden rounded-xl border border-[#EFEAE1] bg-white py-1 shadow-[0_20px_50px_-20px_rgba(43,33,26,0.35)]" data-id="element-3547" data-mp="1rxvwmmyd5v8y">
                    {(['all', ...CONN_CATEGORIES] as const).map(cat => <button key={cat} type="button" onClick={() => {
                setConnCategory(cat as ConnCategory | 'all');
                setConnCatOpen(false);
                setConnPage(1);
              }} className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-[13.5px] font-medium text-[#2B211A] transition-colors hover:bg-[#FFF7F0]" data-id="element-3548" data-mp="26nuvkrxqtaph">
                        <span className="truncate" data-id="element-3549" data-mp="3nbyr8co4d6ld">
                          {cat === 'all' ? 'All categories' : cat}
                        </span>
                        <span className="flex shrink-0 items-center gap-1.5" data-id="element-3550" data-mp="38gisvvgiaoj6">
                          <span className="text-[12px] text-neutral-400" data-id="element-3551" data-mp="3df3r5pc76y4z">
                            {cat === 'all' ? CONNECTIONS.length : CONNECTIONS.filter(c => c.category === cat).length}
                          </span>
                          {connCategory === cat && <Check size={13} strokeWidth={3} className="text-[#EA580C]" data-id="element-3552" data-mp="9z6ffbye1i4j" />}
                        </span>
                      </button>)}
                  </div>
                </>}
            </div>
          </div>

          {/* Connected only: filter by the group that owns the credential.
           Monogram tiles rather than a dropdown — with a handful of groups the
           colours are recognised faster than names are read. */}
          {connTab === 'connected' && <div className="mb-3 flex items-center gap-1.5 overflow-x-auto pb-1" data-id="element-3553" data-mp="2dcnss3vvqwjt">
              <button type="button" onClick={() => {
          setConnGroup('all');
          setConnPage(1);
        }} aria-label="All groups" className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border transition-colors ${connGroup === 'all' ? 'border-[#2B211A] bg-[#F4F0E9] text-[#2B211A]' : 'border-[#EFEAE1] text-neutral-400 hover:border-[#2B211A]'}`} data-id="element-3554" data-mp="28ecl9zt24y4r">
                <Grid3x3 size={15} data-id="element-3555" data-mp="1ph2xy7km30i8" />
              </button>
              {GROUP_NAMES.filter(g => CONNECTIONS.some(c => c.accounts > 0 && CONN_GROUP[c.name] === g)).map(g => {
          const n = CONNECTIONS.filter(c => c.accounts > 0 && CONN_GROUP[c.name] === g).length;
          return <button key={g} type="button" onClick={() => {
            setConnGroup(g);
            setConnPage(1);
          }} title={`${g} · ${n} connected`} className={`relative flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-[11px] font-bold tracking-tight text-white transition-transform ${connGroup === g ? 'ring-2 ring-[#2B211A] ring-offset-2' : 'opacity-75 hover:opacity-100'}`} style={{
            backgroundColor: GROUP_DETAIL[g].color
          }} data-id="element-3556" data-mp="3euy6bflmo71t">
                      {g.split(' ').slice(0, 2).map(w => w[0]).join('')}
                      <span className="absolute -right-1 -top-1 flex h-[15px] min-w-[15px] items-center justify-center rounded-full border border-white bg-[#F4F0E9] px-1 text-[9.5px] font-bold text-[#2B211A]" data-id="element-3557" data-mp="2lg5i345ty9y2">
                        {n}
                      </span>
                    </button>;
        })}
            </div>}

          {(() => {
        const matches = CONNECTIONS.filter(c => connTab === 'all' || (connTab === 'popular' ? c.popular : c.accounts > 0)).filter(c => connTab !== 'connected' || connGroup === 'all' || CONN_GROUP[c.name] === connGroup).filter(c => connCategory === 'all' || c.category === connCategory).filter(c => !q || c.name.toLowerCase().includes(q) || c.desc.toLowerCase().includes(q));
        const pages = Math.max(1, Math.ceil(matches.length / CONN_PAGE_SIZE));
        const page = Math.min(connPage, pages);
        const start = (page - 1) * CONN_PAGE_SIZE;
        const shown = matches.slice(start, start + CONN_PAGE_SIZE);
        return <>
                {/* Count first, then pagination — you need to know how big the
                 result is before deciding whether to page or refine. */}
                <div className="mb-3 flex flex-wrap items-center justify-between gap-3" data-id="element-3558" data-mp="et6fxiea09wh">
                  <p className="text-[12.5px] text-neutral-500" data-id="element-3559" data-mp="9ud4dvijusyy">
                    {matches.length === 0 ? 'No integrations match' : <>
                        Showing {start + 1}–{Math.min(start + CONN_PAGE_SIZE, matches.length)} of{' '}
                        {matches.length} integration
                        {matches.length === 1 ? '' : 's'}
                      </>}
                  </p>
                  {pages > 1 && <div className="flex items-center gap-1" data-id="element-3560" data-mp="1vyxsc7zolib9">
                      <button type="button" onClick={() => setConnPage(p => Math.max(1, p - 1))} disabled={page === 1} className="rounded-lg px-2.5 py-1 text-[12.5px] font-semibold text-[#2B211A] transition-colors hover:bg-[#F4F0E9] disabled:pointer-events-none disabled:text-neutral-300" data-id="element-3561" data-mp="20wg2g1pfq3ku">
                        Previous
                      </button>
                      <span className="px-1.5 text-[12.5px] text-neutral-500" data-id="element-3562" data-mp="1d5tueaffkjau">
                        Page {page} of {pages}
                      </span>
                      <button type="button" onClick={() => setConnPage(p => Math.min(pages, p + 1))} disabled={page === pages} className="rounded-lg px-2.5 py-1 text-[12.5px] font-semibold text-[#2B211A] transition-colors hover:bg-[#F4F0E9] disabled:pointer-events-none disabled:text-neutral-300" data-id="element-3563" data-mp="139h0pcjvy5g3">
                        Next
                      </button>
                    </div>}
                </div>

                {matches.length === 0 ? <div className="rounded-2xl border border-[#EFEAE1] bg-white px-6 py-12 text-center" data-id="element-3564" data-mp="qzhp2ey92k0k">
                    <p className="text-[14px] font-semibold text-[#2B211A]" data-id="element-3565" data-mp="m16dnzsb41g8">
                      Nothing here
                    </p>
                    <p className="mx-auto mt-1.5 max-w-[38ch] text-[13px] leading-relaxed text-neutral-500" data-id="element-3566" data-mp="10qc8lbk435ib">
                      Try another category, or add it yourself with a custom
                      credential.
                    </p>
                  </div> :
          /* Two columns: a catalogue is scanned for a name, and a single
           tall column makes the eye travel much further to find one. */
          <div className="grid gap-2.5 sm:grid-cols-2" data-id="element-3567" data-mp="1eok4rh9bq2sf">
                    {shown.map(c => <div key={c.name} className="group flex items-center gap-3 rounded-2xl border border-[#EFEAE1] bg-white px-4 py-3 transition-colors hover:border-[#DED6C9]" data-id="element-3568" data-mp="1emg3z86q1jwd">
                        <ToolLogo slug={c.slug} size={20} data-id="element-3569" data-mp="3ghi0bkuhj9h2" />
                        <span className="min-w-0 flex-1" data-id="element-3570" data-mp="9z6n561cg2lf">
                          <span className="block truncate text-[14.5px] font-semibold text-[#2B211A]" data-id="element-3571" data-mp="a0ri2wrklnjt">
                            {c.name}
                          </span>
                          {/* Accounts, not a boolean — two accounts is a
                           materially different state. The specific account is
                           held back until hover so the grid stays quiet. */}
                          {c.accounts > 0 ? <>
                              <span className="mt-0.5 flex items-center gap-1.5 text-[12.5px] text-neutral-500 group-hover:hidden" data-id="element-3572" data-mp="wxjorgc6vo72">
                                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" data-id="element-3573" data-mp="11rnvbyhjlu7m" />
                                {c.accounts} account
                                {c.accounts === 1 ? '' : 's'} connected
                              </span>
                              <span className="mt-0.5 hidden truncate text-[12.5px] text-neutral-500 group-hover:block" data-id="element-3574" data-mp="1gnn47bch7ydt">
                                {c.accounts === 1 && c.account ? c.account : `${c.category} · managed in ${CONN_GROUP[c.name] ?? 'Everyone'}`}
                              </span>
                            </> : <span className="mt-0.5 block truncate text-[12.5px] text-neutral-500" data-id="element-3575" data-mp="2r7hv2xn7k94f">
                              {c.desc}
                            </span>}
                        </span>
                        <button type="button" className="shrink-0 rounded-lg border border-transparent px-2.5 py-1.5 text-[12.5px] font-bold text-[#2B211A] opacity-0 transition-opacity hover:border-[#2B211A] group-hover:opacity-100" data-id="element-3576" data-mp="3juugv5ljocwo">
                          {c.accounts > 0 ? 'Manage' : 'Connect'}
                        </button>
                      </div>)}
                  </div>}
              </>;
      })()}
        </>}

      {/* Groups — the counts ARE the content: they tell you at a glance which
       group actually holds the company's knowledge. Metrics sit as quiet
       pills on the right so scanning the names stays effortless. */}
      {view === 'groups' && <List data-id="element-3577" data-mp="3ayu323skga59">
          {GROUP_NAMES.filter(g => !q || g.toLowerCase().includes(q)).map(g => {
        const d = GROUP_DETAIL[g];
        const shared = !g.toLowerCase().includes('private');
        return <Row key={g} data-id="element-3578" data-mp="325h0sb8m3w6i">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-[12px] font-bold tracking-tight text-white" style={{
            backgroundColor: d.color
          }} data-id="element-3579" data-mp="326s469y5b1x3">
                    {g.split(' ').slice(0, 2).map(w => w[0]).join('')}
                  </span>
                  <span className="min-w-0 flex-1" data-id="element-3580" data-mp="3fwvjegylxvd1">
                    <span className="flex items-center gap-2" data-id="element-3581" data-mp="3kw86n2te54g5">
                      <span className="truncate text-[14.5px] font-semibold text-[#2B211A]" data-id="element-3582" data-mp="3kykwe25e4j3r">
                        {g}
                      </span>
                      {!shared && <span className="shrink-0 rounded-full bg-[#F4F0E9] px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wide text-neutral-500" data-id="element-3583" data-mp="29icormb3qhl8">
                          Private
                        </span>}
                    </span>
                    <span className="mt-0.5 block truncate font-mono text-[12px] text-neutral-400" data-id="element-3584" data-mp="1x0tnpsg2y0fb">
                      {d.path}
                    </span>
                  </span>
                  <span className="hidden shrink-0 items-center gap-1 sm:flex" data-id="element-3585" data-mp="2606uuegqecwv">
                    {([[Users, d.members, 'members'], [MessageSquare, d.chats, 'chats'], [Layers, d.tracks, 'tracks'], [KeyRound, d.credentials, 'credentials'], [Copy, d.files, 'files']] as const).map(([Icon, n, label]) => <span key={label} title={`${n} ${label}`} className="inline-flex items-center gap-1 rounded-full bg-[#F7F4EF] px-2 py-1 text-[12px] font-medium text-neutral-500" data-id="element-3586" data-mp="1s0e8evc8miup">
                        <Icon size={12} className="text-neutral-400" data-id="element-3587" data-mp="zia0kjby541b" />
                        {n}
                      </span>)}
                  </span>
                  <button type="button" aria-label={`Edit ${g}`} className="shrink-0 text-neutral-300 transition-colors hover:text-[#2B211A]" data-id="element-3588" data-mp="uie4d69hqgwb">
                    <Pencil size={15} data-id="element-3589" data-mp="3myhq2z1oqdmb" />
                  </button>
                </Row>;
      })}
        </List>}

      {view === 'tracks' && <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3" data-id="element-3590" data-mp="4t51m9l6isp6">
          {TRACKS.filter(t => t.status === 'active').filter(t => !q || t.name.toLowerCase().includes(q)).map(t => <button key={t.name} type="button" className="rounded-2xl border border-[#EFEAE1] bg-white p-4 text-left transition-colors hover:border-[#2B211A]" data-id="element-3591" data-mp="3997idv80adpk">
                <span className="flex items-center justify-between gap-2" data-id="element-3592" data-mp="3i0ga4mzk2uf2">
                  <Folder size={16} className="text-neutral-400" data-id="element-3593" data-mp="3i2km7s7lyzqf" />
                  <StatusDot on data-id="element-3594" data-mp="3d8y7luhvwlrz" />
                </span>
                <span className="mt-3 block truncate text-[14px] font-semibold text-[#2B211A]" data-id="element-3595" data-mp="2pjx5eozna6v5">
                  {t.name}
                </span>
                <span className="mt-0.5 block truncate text-[12px] text-neutral-500" data-id="element-3596" data-mp="2lhsalmxgn46x">
                  {t.group} · {t.type}
                </span>
              </button>)}
        </div>}

      {view === 'chats' && <List data-id="element-3597" data-mp="2g6wno76pvqvg">
          {CHATS.filter(c => !q || c.name.toLowerCase().includes(q) || c.preview.toLowerCase().includes(q)).map(c => <Row key={c.name} data-id="element-3598" data-mp="21ecplkyfc9kv">
                <span className="min-w-0 flex-1" data-id="element-3599" data-mp="26f0p6v0pxyqp">
                  <span className="block truncate text-[14.5px] font-semibold text-[#2B211A]" data-id="element-3600" data-mp="26fto59tfrjya">
                    {c.name}
                  </span>
                  <span className="mt-0.5 block truncate text-[12.5px] text-neutral-500" data-id="element-3601" data-mp="2z4r6fcxndgr1">
                    {c.preview}
                  </span>
                </span>
                <span className="shrink-0 text-[12px] text-neutral-400" data-id="element-3602" data-mp="2k9b64q6h4om7">
                  {c.when}
                </span>
              </Row>)}
        </List>}

      {view === 'workflows' && <List data-id="element-3603" data-mp="18qgswg8uuho3">
          {WORKFLOWS.map(w => <Row key={w.name} data-id="element-3604" data-mp="1dqv05w2mtnl2">
              <span className="min-w-0 flex-1" data-id="element-3605" data-mp="1drxsm8flry33">
                <span className="block truncate text-[14.5px] font-semibold text-[#2B211A]" data-id="element-3606" data-mp="1doicr3e3ul0m">
                  {w.name}
                </span>
                <span className="mt-0.5 block text-[12.5px] text-neutral-500" data-id="element-3607" data-mp="3be71oy9a6lun">
                  {w.steps} steps · {w.runs} runs · {w.success} success · last{' '}
                  {w.last}
                </span>
              </span>
              <button type="button" aria-label={`Run ${w.name}`} className="shrink-0 rounded-lg border border-[#EFEAE1] px-3 py-1.5 text-[12.5px] font-bold text-[#2B211A] transition-colors hover:border-[#2B211A]" data-id="element-3608" data-mp="31odppw5884pv">
                Run
              </button>
            </Row>)}
        </List>}

      {view === 'skills' && <List data-id="element-3609" data-mp="2nqyvme4138rk">
          {SKILLS.map(s => <Row key={s.name} data-id="element-3610" data-mp="2iq1gkaebeuua">
              <GraduationCap size={16} className="shrink-0 text-neutral-400" data-id="element-3611" data-mp="2iqt91ccm8cgc" />
              <span className="min-w-0 flex-1" data-id="element-3612" data-mp="ly172uhab2cf">
                <span className="block truncate text-[14.5px] font-semibold text-[#2B211A]" data-id="element-3613" data-mp="lwq3ovrr3wlu">
                  {s.name}
                </span>
                <span className="mt-0.5 block text-[12.5px] text-neutral-500" data-id="element-3614" data-mp="oih44ym8pg2w">
                  {s.learned}
                </span>
              </span>
              <span className="shrink-0 text-[12px] text-neutral-400" data-id="element-3615" data-mp="tf7lrqdp0jqf">
                {s.used}
              </span>
            </Row>)}
        </List>}

      {view === 'power-ups' && <div className="grid gap-3 sm:grid-cols-2" data-id="element-3616" data-mp="135si175w9c66">
          {POWER_UPS.map(p => <Card key={p.name} data-id="element-3617" data-mp="5jlb3qm4b7qn">
              <div className="flex items-start justify-between gap-3" data-id="element-3618" data-mp="5kd5iy3zazfc">
                <span className="text-[15px] font-semibold text-[#2B211A]" data-id="element-3619" data-mp="ahd28iexupts">
                  {p.name}
                </span>
                <span className={`mt-0.5 flex h-[18px] w-[32px] shrink-0 items-center rounded-full px-[3px] transition-colors ${p.on ? 'justify-end bg-[#2B211A]' : 'justify-start bg-[#E7E1D6]'}`} data-id="element-3620" data-mp="fggascu4ne6i">
                  <span className="h-3 w-3 rounded-full bg-white" data-id="element-3621" data-mp="1qvmrrfux2km7" />
                </span>
              </div>
              <p className="mt-2 text-[13.5px] leading-relaxed text-neutral-500" data-id="element-3622" data-mp="1vty1lql7ov4f">
                {p.desc}
              </p>
            </Card>)}
        </div>}

      {view === 'files' && <List data-id="element-3623" data-mp="1pgakjjhihed4">
          {FILES.filter(f => !q || f.name.toLowerCase().includes(q)).map(f => <Row key={f.name} data-id="element-3624" data-mp="23fjsjvvkd260">
                <FolderOpen size={16} className="shrink-0 text-neutral-400" data-id="element-3625" data-mp="23glckpuk4yzv" />
                <span className="min-w-0 flex-1" data-id="element-3626" data-mp="1yew0d6vlmfa7">
                  <span className="block truncate text-[14.5px] font-semibold text-[#2B211A]" data-id="element-3627" data-mp="1yia2ce0comh4">
                    {f.name}
                  </span>
                  <span className="mt-0.5 block text-[12.5px] text-neutral-500" data-id="element-3628" data-mp="28d23frt55rgn">
                    {f.meta} · {f.group}
                  </span>
                </span>
                {/* Size and who can see it — the two things you check before
                 sharing or deleting, so they sit on the row itself. */}
                <span className="hidden shrink-0 items-center gap-3 text-[12.5px] text-neutral-500 sm:flex" data-id="element-3629" data-mp="2w47e7sk85tk3">
                  <span data-id="element-3630" data-mp="2r6fon571yjj2">{f.size}</span>
                  <span className="inline-flex items-center gap-1.5" data-id="element-3631" data-mp="3aqo2e6y059he">
                    <Copy size={12} className="text-neutral-400" data-id="element-3632" data-mp="3aoaau82j6fel" />
                    {f.visibility}
                  </span>
                </span>
                <span className="shrink-0 text-[12px] text-neutral-400" data-id="element-3633" data-mp="30t081i4b63zr">
                  {f.when}
                </span>
              </Row>)}
        </List>}

      {view === 'tables' && <List data-id="element-3634" data-mp="2r2nl5j90tyqg">
          {TABLES.map(t => <Row key={t.name} data-id="element-3635" data-mp="2r1cgzibnyoyt">
              <Database size={16} className="shrink-0 text-neutral-400" data-id="element-3636" data-mp="2r0aumhp76g1s" />
              <span className="min-w-0 flex-1" data-id="element-3637" data-mp="2vtxaeiqnr01t">
                <span className="block truncate text-[14.5px] font-semibold text-[#2B211A]" data-id="element-3638" data-mp="2i1pjh7m223b1">
                  {t.name}
                </span>
                <span className="mt-0.5 block text-[12.5px] text-neutral-500" data-id="element-3639" data-mp="2n1llx2f3tmoh">
                  {t.rows} · {t.cols} · {t.group}
                </span>
              </span>
              <span className="shrink-0 rounded-full bg-[#F4F0E9] px-2.5 py-1 text-[11.5px] font-medium text-neutral-600" data-id="element-3640" data-mp="2d35roqks5xar">
                {t.source}
              </span>
            </Row>)}
        </List>}

      {view === 'browser' && <List data-id="element-3641" data-mp="1p6iyeivj8nb7">
          {BROWSER_SITES.map(s => <Row key={s.name} data-id="element-3642" data-mp="1p5g8ad61aowc">
              <Globe size={16} className="shrink-0 text-neutral-400" data-id="element-3643" data-mp="1k7ojhs0oriwd" />
              <span className="min-w-0 flex-1" data-id="element-3644" data-mp="1k5v6zw3e64gg">
                <span className="block truncate text-[14.5px] font-semibold text-[#2B211A]" data-id="element-3645" data-mp="1fa65hn7tadhi">
                  {s.name}
                </span>
                <span className="mt-0.5 block text-[12.5px] text-neutral-500" data-id="element-3646" data-mp="2cuj02dudty4k">
                  {s.desc}
                </span>
              </span>
              <span className="shrink-0 text-[12px] text-neutral-400" data-id="element-3647" data-mp="22xo2py9ui3rv">
                {s.runs}
              </span>
            </Row>)}
        </List>}

      {/* Viewer — one live run, because that is the whole point of the screen. */}
      {view === 'viewer' && <div className="overflow-hidden rounded-2xl border border-[#EFEAE1] bg-white" data-id="element-3648" data-mp="rh66nza5u35r">
          <div className="flex items-center justify-between gap-3 border-b border-[#F4F0E9] px-5 py-3.5" data-id="element-3649" data-mp="mmk5eoxslqze">
            <span className="flex items-center gap-2" data-id="element-3650" data-mp="2y5ycqsky09bn">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#EA580C]" data-id="element-3651" data-mp="337xexz4y5070" />
              <span className="text-[14.5px] font-semibold text-[#2B211A]" data-id="element-3652" data-mp="385p6ut5lcmb7">
                New lead → enriched CRM record
              </span>
            </span>
            <span className="text-[12.5px] text-neutral-400" data-id="element-3653" data-mp="3cyib7fx5dsmr">
              Step 4 of 6 · 00:38
            </span>
          </div>
          <ol className="divide-y divide-[#F4F0E9]" data-id="element-3654" data-mp="3lwlzul3fj5is">
            {[{
          label: 'Read the inbound form submission',
          state: 'done'
        }, {
          label: 'Looked up the company domain',
          state: 'done'
        }, {
          label: 'Pulled headcount and funding from the web',
          state: 'done'
        }, {
          label: 'Writing the record to Salesforce',
          state: 'running'
        }, {
          label: 'Assigning an owner by territory',
          state: 'pending'
        }, {
          label: 'Posting a summary to #sales',
          state: 'pending'
        }].map(s => <li key={s.label} className="flex items-center gap-3 px-5 py-3" data-id="element-3655" data-mp="29k4a73t1jb8">
                {s.state === 'done' ? <Check size={14} strokeWidth={3} className="shrink-0 text-emerald-500" data-id="element-3656" data-mp="gyg8ft2qqc6k" /> : s.state === 'running' ? <span className="h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-[#EA580C] border-t-transparent" data-id="element-3657" data-mp="c1g2rj9szbfz" /> : <span className="h-3.5 w-3.5 shrink-0 rounded-full border-2 border-[#EFEAE1]" data-id="element-3658" data-mp="3kgqab6wgp8v5" />}
                <span className={`text-[14px] ${s.state === 'pending' ? 'text-neutral-400' : 'text-[#2B211A]'}`} data-id="element-3659" data-mp="3fiogbqxhzp4w">
                  {s.label}
                </span>
              </li>)}
          </ol>
          <div className="flex items-center justify-between gap-3 border-t border-[#F4F0E9] px-5 py-3" data-id="element-3660" data-mp="3pdq5unvoqt80">
            <span className="text-[12.5px] text-neutral-500" data-id="element-3661" data-mp="19mlucj5j677z">
              412 credits spent so far
            </span>
            <button type="button" className="inline-flex items-center gap-1 text-[12.5px] font-bold text-[#2B211A] transition-colors hover:text-[#EA580C]" data-id="element-3662" data-mp="14r6hnuj9ehd5">
              Open full trace
              <ArrowUpRight size={13} data-id="element-3663" data-mp="1h8zha0de6e0d" />
            </button>
          </div>
        </div>}
    </div>;
}